import axios from 'axios';

import { IPublicMapSubmissionsApiResponse } from '@containers/common/CitizenMapView/types';

import { getCitizenAuthHeaders } from '@private-crm-app/utils';
import { getIdToken } from '@utils/AuthUtil';

// ---------------------------------------------------------------------------
// Public (unauthenticated, token-based) helpers
// ---------------------------------------------------------------------------

export function buildPublicMapApiUrl(): string {
  const apiUrl = process.env.API_URL;
  if (!apiUrl) {
    throw new Error('[publicMap] process.env.API_URL is not set');
  }
  try {
    new URL(apiUrl);
  } catch {
    throw new Error(`[publicMap] process.env.API_URL "${apiUrl}" is not a valid URL`);
  }

  return `${apiUrl}/crm`;
}

export async function fetchMapConfig(crmToken: string, signal?: AbortSignal): Promise<IMapConfig> {
  const baseUrl = buildPublicMapApiUrl();
  const { data } = await axios.get<IMapConfig>(`${baseUrl}/${encodeURIComponent(crmToken)}/map-config`, { signal });

  return data;
}

export interface IPublicMapIssuesFilters {
  categoryIds?: number[];
  status?: string;
  createdAfterDate?: string;
  createdBeforeDate?: string;
}


export async function fetchPublicMapIssues(
  crmToken: string,
  bbox?: IPublicMapBbox,
  filters?: IPublicMapIssuesFilters
): Promise<IPublicMapSubmissionsApiResponse> {
  const baseUrl = buildPublicMapApiUrl();
  const params: Record<string, number | number[] | string | undefined> = {};
  if (bbox) {
    params.bboxMinLat = bbox.bboxMinLat;
    params.bboxMaxLat = bbox.bboxMaxLat;
    params.bboxMinLng = bbox.bboxMinLng;
    params.bboxMaxLng = bbox.bboxMaxLng;
  }
  if (filters?.categoryIds?.length) {
    params.categoryIds = filters.categoryIds;
  }
  if (filters?.status) {
    params.status = filters.status;
  }
  if (filters?.createdAfterDate) {
    params.createdAfterDate = filters.createdAfterDate;
  }
  if (filters?.createdBeforeDate) {
    params.createdBeforeDate = filters.createdBeforeDate;
  }
  const { data } = await axios.get<IPublicMapSubmissionsApiResponse>(
    `${baseUrl}/${encodeURIComponent(crmToken)}/issues/public-map`,
    {
      params: Object.keys(params).length ? params : undefined,
      headers: getCitizenAuthHeaders(),
    }
  );

  return data;
}

export async function followPublicMapIssue(
  crmToken: string,
  issueId: number
): Promise<{ followerCount: number }> {
  const baseUrl = buildPublicMapApiUrl();
  const { data } = await axios.post<{ followerCount: number }>(
    `${baseUrl}/${encodeURIComponent(crmToken)}/issues/${issueId}/follow`,
    {},
    { headers: getCitizenAuthHeaders() }
  );

  return data;
}

export async function unfollowPublicMapIssue(
  crmToken: string,
  issueId: number
): Promise<{ followerCount: number }> {
  const baseUrl = buildPublicMapApiUrl();
  const { data } = await axios.delete<{ followerCount: number }>(
    `${baseUrl}/${encodeURIComponent(crmToken)}/issues/${issueId}/follow`,
    { headers: getCitizenAuthHeaders() }
  );

  return data;
}

export interface IPublicMapIssueDetailResponse {
  id: number;
  agencyId: number;
  description: string;
  crmToken: string;
  category: { id: number; name: string };
  issueSummary: {
    CSNumber: string;
    CSAge: number;
    status: string;
    createdAt: string;
    assignee: { id: number; firstName: string; lastName: string; isActive: boolean } | null;
  };
  location: string | null;
  coordinates: {
    latitude: number;
    longitude: number;
    userPinLatitude: number | null;
    userPinLongitude: number | null;
  } | null;
  attachments: Array<{ id: number; fileName: string; fileURL: string; mimeType?: string }>;
  followerCount: number;
  isFollowing: boolean | null;
  isSubmitter: boolean | null;
  followerEmails: IFollowerUpdateEmail[];
}

export async function fetchPublicMapIssueById(
  crmToken: string,
  crmIssueId: string,
  signal?: AbortSignal
): Promise<IPublicMapIssueDetailResponse> {
  const baseUrl = buildPublicMapApiUrl();
  const { data } = await axios.get<IPublicMapIssueDetailResponse>(
    `${baseUrl}/${encodeURIComponent(crmToken)}/issues/public-map/${encodeURIComponent(crmIssueId)}`,
    { signal, headers: getCitizenAuthHeaders() }
  );

  return data;
}

// ---------------------------------------------------------------------------
// Staff preview (authenticated, agency-scoped) helpers
// These bypass the isPublicMapEnabled gate and require a staff JWT.
// ---------------------------------------------------------------------------

export function buildStaffPreviewApiUrl(): string {
  const apiUrl = process.env.API_URL;
  if (!apiUrl) {
    throw new Error('[publicMap] process.env.API_URL is not set');
  }

  return `${apiUrl}/agencies/crm`;
}

function staffPreviewHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getIdToken()}` };
}

export async function fetchStaffPreviewMapConfig(signal?: AbortSignal): Promise<IMapConfig> {
  const { data } = await axios.get<IMapConfig>(`${buildStaffPreviewApiUrl()}/map-config`, {
    signal,
    headers: staffPreviewHeaders(),
  });

  return data;
}

export async function fetchStaffPreviewPublicMapIssues(
  bbox?: IPublicMapBbox,
  filters?: IPublicMapIssuesFilters
): Promise<IPublicMapSubmissionsApiResponse> {
  const params: Record<string, number | number[] | string | undefined> = {};
  if (bbox) {
    params.bboxMinLat = bbox.bboxMinLat;
    params.bboxMaxLat = bbox.bboxMaxLat;
    params.bboxMinLng = bbox.bboxMinLng;
    params.bboxMaxLng = bbox.bboxMaxLng;
  }
  if (filters?.categoryIds?.length) {
    params.categoryIds = filters.categoryIds;
  }
  if (filters?.status) {
    params.status = filters.status;
  }
  if (filters?.createdAfterDate) {
    params.createdAfterDate = filters.createdAfterDate;
  }
  if (filters?.createdBeforeDate) {
    params.createdBeforeDate = filters.createdBeforeDate;
  }
  const { data } = await axios.get<IPublicMapSubmissionsApiResponse>(`${buildStaffPreviewApiUrl()}/issues/public-map`, {
    params: Object.keys(params).length ? params : undefined,
    headers: staffPreviewHeaders(),
  });

  return data;
}
